import React from 'react';

const SVG_HEIGHT = 280;
const MARGIN = { top: 22, right: 20, bottom: 58, left: 54 };
const BAR_WIDTH = 28;
const BAR_GAP = 20;
const ALL_SCHOOLS = 'ALL';
const SCHOOL_OPTIONS = [
  { school: ALL_SCHOOLS, schoolLabel: 'All Schools' },
  { school: 'MMA', schoolLabel: 'Mountainside' },
  { school: 'LSMA', schoolLabel: 'Lakeside' },
  { school: 'TMA', schoolLabel: 'Treeside' },
  { school: 'MOR', schoolLabel: 'Morningside' },
  { school: 'PSMA', schoolLabel: 'Parkside' },
];
const INCLUDED_SCHOOLS = new Set(SCHOOL_OPTIONS.slice(1).map(({ school }) => school));

const formatHours = (value) => `${Number(value || 0).toFixed(1)} hrs`;
const roundToTwo = (value) => Math.round((value + Number.EPSILON) * 100) / 100;

const TeacherHoursPerSchoolChart = ({ data, loading, error }) => {
  const includedRows = React.useMemo(() => (
    (Array.isArray(data) ? data : []).filter((row) => INCLUDED_SCHOOLS.has(row?.school))
  ), [data]);
  const allSchoolRows = React.useMemo(() => {
    const monthlyRows = new Map();
    includedRows.forEach((row) => {
      if (!monthlyRows.has(row.monthKey)) {
        monthlyRows.set(row.monthKey, {
          school: ALL_SCHOOLS,
          schoolLabel: 'All Schools',
          monthKey: row.monthKey,
          label: row.label,
          date: row.date,
          totalMinutes: 0,
          teacherCount: 0,
          lessonCount: 0,
        });
      }
      const month = monthlyRows.get(row.monthKey);
      month.totalMinutes += Number.isFinite(row.totalMinutes)
        ? row.totalMinutes
        : Number(row.totalHours || 0) * 60;
      month.teacherCount += Number(row.teacherCount || 0);
      month.lessonCount += Number(row.lessonCount || 0);
    });

    return Array.from(monthlyRows.values()).map((row) => ({
      ...row,
      totalHours: roundToTwo(row.totalMinutes / 60),
      averageHours: row.teacherCount
        ? roundToTwo(row.totalMinutes / 60 / row.teacherCount)
        : 0,
    }));
  }, [includedRows]);
  const [selectedSchool, setSelectedSchool] = React.useState(ALL_SCHOOLS);
  const [tooltip, setTooltip] = React.useState(null);

  const schoolRows = React.useMemo(() => (
    (selectedSchool === ALL_SCHOOLS ? allSchoolRows : includedRows)
      .filter((row) => selectedSchool === ALL_SCHOOLS || row?.school === selectedSchool)
      .sort((left, right) => left.date.getTime() - right.date.getTime())
      .slice(-24)
  ), [allSchoolRows, includedRows, selectedSchool]);

  if (loading) {
    return <div className='text-sm text-slate-500'>Calculating teacher hours from timesheets…</div>;
  }
  if (error) {
    return <div className='text-sm text-red-500'>Unable to load teacher hours: {error}</div>;
  }
  if (!includedRows.length) {
    return <div className='text-sm text-slate-500'>No timesheet teaching hours are available yet.</div>;
  }

  const latest = schoolRows[schoolRows.length - 1] || null;
  if (!latest) {
    return (
      <div className='flex flex-col gap-4'>
        <SchoolSelector value={selectedSchool} onChange={setSelectedSchool} />
        <div className='text-sm text-slate-500'>No timesheet teaching hours are available for this school yet.</div>
      </div>
    );
  }
  const maxAverage = Math.max(...schoolRows.map((row) => row.averageHours), 1);
  const innerHeight = SVG_HEIGHT - MARGIN.top - MARGIN.bottom;
  const chartWidth = Math.max(
    720,
    MARGIN.left + MARGIN.right + schoolRows.length * (BAR_WIDTH + BAR_GAP)
  );

  return (
    <div className='flex flex-col gap-5'>
      <div className='flex flex-wrap items-end justify-between gap-4'>
        <div>
          <SchoolSelector value={selectedSchool} onChange={setSelectedSchool} />
        </div>
        <div className='text-right'>
          <div className='text-xs font-bold uppercase tracking-wider text-slate-400'>Latest timesheet month</div>
          <div className='mt-1 text-sm font-semibold text-slate-700'>{latest.label}</div>
        </div>
      </div>

      <div className='grid grid-cols-1 gap-3 sm:grid-cols-3'>
        <div className='rounded-xl border border-solid border-indigo-100 bg-indigo-50 px-4 py-3'>
          <div className='text-[10px] font-bold uppercase tracking-wider text-indigo-500'>Average per teacher</div>
          <div className='mt-1 text-2xl font-extrabold tabular-nums text-indigo-900'>{formatHours(latest.averageHours)}</div>
        </div>
        <div className='rounded-xl border border-solid border-sky-100 bg-sky-50 px-4 py-3'>
          <div className='text-[10px] font-bold uppercase tracking-wider text-sky-600'>Total taught</div>
          <div className='mt-1 text-2xl font-extrabold tabular-nums text-sky-900'>{formatHours(latest.totalHours)}</div>
          <div className='mt-1 text-xs text-sky-700'>{latest.lessonCount.toLocaleString()} lessons</div>
        </div>
        <div className='rounded-xl border border-solid border-emerald-100 bg-emerald-50 px-4 py-3'>
          <div className='text-[10px] font-bold uppercase tracking-wider text-emerald-600'>Teachers included</div>
          <div className='mt-1 text-2xl font-extrabold tabular-nums text-emerald-900'>{latest.teacherCount}</div>
          <div className='mt-1 text-xs text-emerald-700'>with recorded lessons</div>
        </div>
      </div>

      <div className='relative overflow-x-auto rounded-xl border border-solid border-slate-200 bg-white'>
        <svg
          width={chartWidth}
          height={SVG_HEIGHT}
          role='img'
          aria-label={`Average monthly teaching hours per teacher at ${latest.schoolLabel}`}
          onMouseLeave={() => setTooltip(null)}
        >
          {[0, 0.25, 0.5, 0.75, 1].map((tick) => {
            const value = maxAverage * tick;
            const y = MARGIN.top + innerHeight * (1 - tick);
            return (
              <g key={tick}>
                <line
                  x1={MARGIN.left}
                  x2={chartWidth - MARGIN.right}
                  y1={y}
                  y2={y}
                  stroke='rgba(148, 163, 184, 0.22)'
                />
                <text
                  x={MARGIN.left - 8}
                  y={y}
                  textAnchor='end'
                  dominantBaseline='middle'
                  fill='#94A3B8'
                  fontSize='10'
                >
                  {value.toFixed(value < 10 ? 1 : 0)}h
                </text>
              </g>
            );
          })}

          {schoolRows.map((row, index) => {
            const x = MARGIN.left + index * (BAR_WIDTH + BAR_GAP);
            const barHeight = (row.averageHours / maxAverage) * innerHeight;
            const y = MARGIN.top + innerHeight - barHeight;
            const isLatest = index === schoolRows.length - 1;
            return (
              <g key={`${row.school}-${row.monthKey}`}>
                <rect
                  x={x}
                  y={y}
                  width={BAR_WIDTH}
                  height={barHeight}
                  rx='7'
                  fill={isLatest ? '#4F46E5' : '#A5B4FC'}
                />
                <rect
                  x={x - BAR_GAP / 2}
                  y={MARGIN.top}
                  width={BAR_WIDTH + BAR_GAP}
                  height={innerHeight}
                  fill='transparent'
                  tabIndex='0'
                  aria-label={`${row.label}: ${formatHours(row.averageHours)} per teacher`}
                  onMouseEnter={() => setTooltip({ row, x: x + BAR_WIDTH / 2 })}
                  onMouseMove={() => setTooltip({ row, x: x + BAR_WIDTH / 2 })}
                  onFocus={() => setTooltip({ row, x: x + BAR_WIDTH / 2 })}
                  onBlur={() => setTooltip(null)}
                />
                <text
                  x={x + BAR_WIDTH / 2}
                  y={SVG_HEIGHT - MARGIN.bottom + 20}
                  textAnchor='middle'
                  fill='#64748B'
                  fontSize='10'
                >
                  {row.date.toLocaleDateString('en-US', { month: 'short' })}
                </text>
                <text
                  x={x + BAR_WIDTH / 2}
                  y={SVG_HEIGHT - MARGIN.bottom + 34}
                  textAnchor='middle'
                  fill='#94A3B8'
                  fontSize='9'
                >
                  {String(row.date.getFullYear()).slice(-2)}
                </text>
              </g>
            );
          })}
        </svg>

        {tooltip && (
          <div
            className='pointer-events-none absolute top-3 -translate-x-1/2 rounded-xl border border-solid border-slate-200 bg-slate-900 px-3 py-2 text-xs text-white shadow-lg'
            style={{ left: `${Math.min(Math.max(tooltip.x, 85), chartWidth - 85)}px` }}
          >
            <div className='font-bold'>{tooltip.row.label}</div>
            <div className='mt-1 tabular-nums'>{formatHours(tooltip.row.averageHours)} average</div>
            <div className='mt-0.5 text-slate-300'>{formatHours(tooltip.row.totalHours)} total · {tooltip.row.teacherCount} teachers</div>
          </div>
        )}
      </div>

      <p className='m-0 text-xs leading-5 text-slate-500'>
        Based on lesson durations recorded in submitted timesheets. Teachers are included in a month only when they recorded at least one lesson; cancellations are excluded.
      </p>
    </div>
  );
};

const SchoolSelector = ({ value, onChange }) => (
  <div>
    <label
      htmlFor='teacher-hours-school'
      className='block text-xs font-bold uppercase tracking-wider text-slate-500'
    >
      School
    </label>
    <select
      id='teacher-hours-school'
      value={value}
      onChange={(event) => onChange(event.target.value)}
      className='mt-1 min-w-[220px] cursor-pointer rounded-lg border border-solid border-slate-300 bg-white px-3 py-2 text-sm font-semibold text-slate-800'
    >
      {SCHOOL_OPTIONS.map((school) => (
        <option key={school.school} value={school.school}>{school.schoolLabel}</option>
      ))}
    </select>
  </div>
);

export default TeacherHoursPerSchoolChart;
